import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, FileText, Clock, CheckCircle2, Loader2, Download, Inbox, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, API } from "@/lib/api";

const ESTADOS = ["Radicada", "En trámite", "Resuelta"];
const estadoStyle = {
  "Radicada": "bg-amber-500 hover:bg-amber-500",
  "En trámite": "bg-blue-500 hover:bg-blue-500",
  "Resuelta": "bg-emerald-500 hover:bg-emerald-500",
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("Todas");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [respuesta, setRespuesta] = useState("");
  const [nuevoEstado, setNuevoEstado] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([api.get("/pqr/stats/summary"), api.get("/pqr")]);
      setStats(s.data);
      setItems(l.data);
    } catch {
      toast.error("No se pudieron cargar las PQR.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = (pqr) => {
    setSelected(pqr);
    setRespuesta(pqr.respuesta || "");
    setNuevoEstado(pqr.estado);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/pqr/${selected.id}`, { estado: nuevoEstado, respuesta });
      setItems((prev) => prev.map((p) => (p.id === data.id ? data : p)));
      setSelected(data);
      toast.success("PQR actualizada.");
      load();
    } catch {
      toast.error("No se pudo actualizar.");
    } finally {
      setSaving(false);
    }
  };

  const doLogout = () => { logout(); navigate("/admin/login"); };

  const filtered = items.filter((p) => {
    const matchEstado = filter === "Todas" || p.estado === filter;
    const q = query.toLowerCase();
    const matchQuery = !q || p.radicado.toLowerCase().includes(q) || p.nombre.toLowerCase().includes(q) || p.identificacion.includes(q);
    return matchEstado && matchQuery;
  });

  const cards = [
    { label: "Total PQR", value: stats?.total ?? 0, icon: Inbox, color: "text-primary" },
    { label: "Radicadas", value: stats?.radicadas ?? 0, icon: FileText, color: "text-amber-500" },
    { label: "En trámite", value: stats?.en_tramite ?? 0, icon: Clock, color: "text-blue-500" },
    { label: "Resueltas", value: stats?.resueltas ?? 0, icon: CheckCircle2, color: "text-emerald-500" },
  ];

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight">Panel de PQR</h1>
          <p className="text-muted-foreground text-sm">Bienvenido, {user?.name}. Gestiona las peticiones, quejas, reclamos y recursos.</p>
        </div>
        <Button variant="outline" onClick={doLogout} className="rounded-full gap-2" data-testid="admin-logout"><LogOut className="h-4 w-4" /> Cerrar sesión</Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {cards.map((c) => (
          <div key={c.label} className="rounded-2xl border border-border bg-card p-5" data-testid={`stat-${c.label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="flex items-center justify-between mb-2"><span className="text-xs uppercase tracking-wider text-muted-foreground">{c.label}</span><c.icon className={`h-4 w-4 ${c.color}`} /></div>
            <div className="font-display text-3xl font-black">{c.value}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar por radicado, nombre o identificación..." className="pl-10 rounded-full" data-testid="admin-search" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-44 rounded-full" data-testid="admin-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{["Todas", ...ESTADOS].map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="grid place-items-center py-20 text-muted-foreground"><Loader2 className="h-8 w-8 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-border py-20 text-center text-muted-foreground">
          <Inbox className="h-10 w-10 mx-auto mb-3 opacity-50" /> No hay PQR que coincidan.
        </div>
      ) : (
        <div className="rounded-3xl border border-border overflow-hidden overflow-x-auto">
          <table className="w-full text-left text-sm min-w-[720px]">
            <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-5 py-4">Radicado</th><th className="px-5 py-4">Usuario</th><th className="px-5 py-4">Tipo</th>
                <th className="px-5 py-4">Fecha</th><th className="px-5 py-4">Estado</th><th className="px-5 py-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-t border-border hover:bg-muted/30 transition-colors" data-testid={`pqr-row-${p.radicado}`}>
                  <td className="px-5 py-4 font-mono font-semibold text-primary">{p.radicado}</td>
                  <td className="px-5 py-4"><div className="font-medium">{p.nombre}</div><div className="text-xs text-muted-foreground">{p.identificacion}</div></td>
                  <td className="px-5 py-4">{p.tipo_pqr}{p.relacionada_facturacion && <span className="block text-xs text-muted-foreground">Facturación</span>}</td>
                  <td className="px-5 py-4 text-muted-foreground">{new Date(p.fecha_radicacion).toLocaleDateString("es-CO")}</td>
                  <td className="px-5 py-4"><Badge className={estadoStyle[p.estado]}>{p.estado}</Badge></td>
                  <td className="px-5 py-4"><Button size="sm" variant="outline" className="rounded-full" onClick={() => openDetail(p)} data-testid={`pqr-view-${p.radicado}`}>Gestionar</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pqr-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-primary">{selected.radicado}</span>
                  <Badge className={estadoStyle[selected.estado]}>{selected.estado}</Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <Detail label="Usuario" value={selected.nombre} />
                  <Detail label="Identificación" value={`${selected.tipo_identificacion} ${selected.identificacion}`} />
                  <Detail label="Correo" value={selected.email} />
                  <Detail label="Teléfono" value={selected.telefono} />
                  <Detail label="Tipo de PQR" value={selected.tipo_pqr} />
                  <Detail label="Recurso" value={selected.recurso || "N/A"} />
                  <Detail label="Facturación" value={selected.relacionada_facturacion ? "Sí" : "No"} />
                  <Detail label="Plazo" value={selected.plazo_respuesta} />
                </div>
                <Detail label="Motivo" value={selected.motivo} />
                <div><div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Descripción</div><p className="rounded-xl bg-muted/50 p-3">{selected.descripcion}</p></div>

                <div className="grid sm:grid-cols-2 gap-4 pt-2">
                  <div className="space-y-2">
                    <label className="text-xs uppercase tracking-wider text-muted-foreground">Cambiar estado</label>
                    <Select value={nuevoEstado} onValueChange={setNuevoEstado}>
                      <SelectTrigger data-testid="pqr-estado-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{ESTADOS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <a href={`${API}/pqr/${selected.radicado}/constancia`} target="_blank" rel="noopener noreferrer" className="w-full">
                      <Button variant="outline" className="w-full rounded-full gap-2"><Download className="h-4 w-4" /> Constancia PDF</Button>
                    </a>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-xs uppercase tracking-wider text-muted-foreground">Respuesta al usuario</label>
                  <Textarea rows={4} value={respuesta} onChange={(e) => setRespuesta(e.target.value)} placeholder="Escribe la respuesta oficial a esta PQR..." data-testid="pqr-respuesta" />
                </div>
                <Button onClick={save} disabled={saving} className="w-full rounded-full font-semibold gap-2" data-testid="pqr-save">
                  {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Guardando...</> : "Guardar cambios"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Detail = ({ label, value }) => (
  <div><div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div><div className="font-medium">{value}</div></div>
);
